import { Badge } from "@/components/shadcn/badge";
import { Card, CardContent } from "@/components/shadcn/card";
import { CalendarDays, Gift, Users } from "lucide-react";
import { useFormatter, useTranslations } from "next-intl";
import Image from "next/image";
import { RequirementBadge } from "../requirement-badge";
import { GiveawayJoinButton } from "./giveaway-join-button";
import { GiveawayEventWithUserStatus } from "./types";

export function GiveawayUpcomingCard({
    event,
}: {
    event: GiveawayEventWithUserStatus;
}) {
    const t = useTranslations("events");
    const format = useFormatter();

    const eventDate = new Date(event.date);
    const dateStr = format.dateTime(eventDate, "long");
    const timeStr = format.dateTime(eventDate, "time");

    return (
        <Card className="h-full">
            <CardContent className="flex h-full flex-col gap-4 sm:flex-row sm:gap-6">
                <div className="relative aspect-square shrink-0 overflow-hidden rounded-lg sm:w-2/5">
                    {event.imageUrl ? (
                        <Image
                            src={event.imageUrl}
                            alt={event.title}
                            fill
                            className="object-cover"
                            sizes="(max-width: 640px) 100vw, 40vw"
                        />
                    ) : (
                        <div className="bg-muted flex size-full items-center justify-center">
                            <Gift className="text-muted-foreground size-12" />
                        </div>
                    )}
                </div>

                <div className="flex min-w-0 flex-1 flex-col justify-between gap-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <Badge className="bg-primary/10 text-primary">
                            <CalendarDays />
                            {t("lastEntry")}: {dateStr} · {timeStr}
                        </Badge>
                        <div className="flex flex-wrap items-center gap-2">
                            <Badge>
                                <Users />
                                {event.participantCount}/{event.capacity}
                            </Badge>
                            <RequirementBadge isPremium={event.isPremium} />
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <h3 className="text-sm font-medium">{event.title}</h3>
                        {event.description && (
                            <p className="text-muted-foreground text-xs whitespace-pre-line">
                                {event.description}
                            </p>
                        )}
                    </div>

                    <div className="sm:max-w-48">
                        <GiveawayJoinButton event={event} />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
